/**
 * Rémunération ou dividendes : comment se payer quand on a une société.
 *
 * LA QUESTION DU SOLOPRENEUR EN SOCIÉTÉ, ET CELLE QU'AUCUN OUTIL DE LA RUBRIQUE
 * NE TRAITAIT. Le comparateur de régimes suppose que le dirigeant se verse tout
 * en rémunération. Or le vrai choix, une fois la société créée, est le partage :
 * ce que la société verse en rémunération passe par les cotisations et
 * l'impôt sur le revenu ; ce qu'elle garde paie l'impôt sur les sociétés, puis
 * se distribue en dividendes, qui ont leur propre fiscalité. Le bon partage
 * n'est ni « tout en salaire » ni « tout en dividendes » : il se calcule.
 *
 * DEUX FORMES, DEUX RÈGLES QUI N'ONT RIEN À VOIR
 *
 *   - SASU : le président est assimilé salarié. Ses dividendes ne supportent
 *     aucune cotisation, seulement le prélèvement forfaitaire unique de 31,4 %
 *     (12,8 % d'impôt, 18,6 % de prélèvements sociaux) ou, sur option, le
 *     barème après un abattement de 40 %.
 *   - EURL, gérant associé unique : il est travailleur indépendant. La part de
 *     ses dividendes qui dépasse 10 % du capital, des primes d'émission et des
 *     sommes laissées en compte courant s'ajoute à sa rémunération pour le
 *     calcul des cotisations, abattement de 26 % compris (article L. 136-3, II,
 *     2° du code de la sécurité sociale, qui a repris la règle de l'ancien
 *     L. 131-6 ; notice Urssaf des revenus 2026, § 4). Avec
 *     1 000 € de capital, c'est presque tout.
 *
 * LES HYPOTHÈSES, ASSUMÉES ET AFFICHÉES PAR L'OUTIL
 *
 *   1. Tout le bénéfice qui reste après l'impôt sur les sociétés est distribué
 *      la même année. La réserve légale, quelques dizaines d'euros avec un
 *      petit capital, n'est pas retenue.
 *   2. Les cotisations dues sur les dividendes d'un gérant d'EURL sont payées
 *      par lui, sur ses dividendes, et ne sont déduites nulle part. C'est le
 *      sens prudent : il ne flatte pas les dividendes.
 *   3. À l'option pour le barème, la part de CSG déductible sur les dividendes
 *      n'est pas retirée du revenu imposable : même sens prudent.
 *   4. Le coût d'un président de SASU reste un plancher : la cotisation
 *      accidents du travail n'est pas comptée (voir businessStructure.js).
 *
 * L'ARGENT N'EST PAS TOUT, ET L'OUTIL LE DIT. Un euro versé en dividendes par
 * une SASU ne cotise à rien : ni retraite, ni indemnités journalières. Le
 * partage qui laisse le plus d'argent est souvent celui qui protège le moins.
 */

import {
    apresFraisProfessionnels,
    PFU_IR,
    PFU_SOCIAL,
    cotisationsAssimileSalarie,
    cotisationsIndependant,
    impotSocietes,
    remunerationPour,
} from "./businessStructure.js";
import { impotSurLeRevenu } from "./incomeTax.js";

/**
 * EURL : la part des dividendes au-delà de laquelle ils cotisent, en fraction
 * du capital, des primes d'émission et du compte courant d'associé.
 */
export const SEUIL_DIVIDENDES_EURL = 0.1;

/** Abattement sur les dividendes quand on opte pour le barème. */
export const ABATTEMENT_DIVIDENDES_BAREME = 0.4;

/** Le pas d'exploration du partage : un point de rémunération à la fois. */
export const PAS_PARTAGE = 0.01;

/**
 * Écart sous lequel on refuse de désigner un partage : mille euros par an,
 * comme le comparateur de régimes, et pour la même raison.
 */
export const ECART_NEGLIGEABLE = 1000;

export const FORMES = ["sasu", "eurl"];

/**
 * Le salaire qui valide un trimestre de retraite en 2026 : 150 fois le SMIC
 * horaire du 1er janvier, 12,02 €. Circulaire Cnav 2025-33 du 23 décembre
 * 2025 ; Service-Public F1761. Quatre trimestres au plus par an.
 */
export const SALAIRE_UN_TRIMESTRE_2026 = 1803;

/** Les trimestres qu'un salaire brut d'assimilé salarié valide dans l'année. */
export function trimestresSalarie(brut) {
    return Math.min(4, Math.floor(positif(brut) / SALAIRE_UN_TRIMESTRE_2026));
}

/**
 * Travailleur indépendant : les trimestres se comptent sur la cotisation de
 * retraite de base payée, pas sur le revenu. Montants de 2026 pour un, deux,
 * trois et quatre trimestres, circulaire Cnav 2025-33, § 2.2.
 */
export const COTISATION_TRIMESTRES_INDEPENDANT_2026 = [322, 644, 967, 1289];

/** Les trimestres qu'une cotisation de retraite de base d'indépendant valide. */
export function trimestresIndependant(cotisationVieillesse) {
    return COTISATION_TRIMESTRES_INDEPENDANT_2026.filter((seuil) => cotisationVieillesse >= seuil)
        .length;
}

const positif = (x) => Math.max(0, x);

/** Ce qu'un revenu ajoute à l'impôt du foyer, et rien d'autre. */
function impotAjoute(revenuImposable, { autresRevenus = 0, ...foyer }) {
    const avec = impotSurLeRevenu(positif(autresRevenus + revenuImposable), foyer);
    const sans = impotSurLeRevenu(positif(autresRevenus), foyer);
    return positif(avec - sans);
}

/**
 * La rémunération : ce qu'elle coûte à la société, ce qu'il en reste avant
 * impôt, et la part qui entre dans le revenu imposable. Exportée pour les
 * explications (lib/tools/explanations.js), qui en relisent les étapes.
 */
export function voletRemuneration({ forme, enveloppeRemuneration, activite, artisan }) {
    if (forme === "sasu") {
        const brut = remunerationPour(
            enveloppeRemuneration,
            (r) => cotisationsAssimileSalarie(r).coutEmployeur,
        );
        const c = cotisationsAssimileSalarie(brut);
        return {
            brut,
            cout: c.coutEmployeur,
            cotisations: c.patronales + c.salariales,
            avantImpot: c.net,
            imposable:
                brut > 0
                    ? apresFraisProfessionnels(c.net + c.nonDeductible)
                    : 0,
            detail: c,
        };
    }

    // EURL : l'assiette du gérant est l'enveloppe entière, avant déduction des
    // cotisations, comme pour une entreprise individuelle au réel. Les
    // cotisations minimales restent dues sans rémunération : la société les
    // paie, ce qui peut porter son coût au-dessus de l'enveloppe.
    const c = cotisationsIndependant({
        revenuAvantCotisations: enveloppeRemuneration,
        activite,
        artisan,
    });
    const remuneration = positif(enveloppeRemuneration - c.total);
    return {
        brut: remuneration,
        cout: Math.max(enveloppeRemuneration, c.total),
        cotisations: c.total,
        avantImpot: remuneration,
        imposable:
            remuneration > 0
                ? apresFraisProfessionnels(remuneration + c.nonDeductible)
                : 0,
        detail: c,
    };
}

/**
 * Un partage donné : `part` est la fraction de l'enveloppe consacrée à la
 * rémunération, cotisations comprises ; le reste paie l'impôt sur les sociétés
 * et se distribue.
 *
 * @param enveloppe  ce que la société gagne avant de payer son dirigeant
 * @param capital    EURL : capital, primes d'émission et compte courant
 * @param imposition « pfu », « bareme » ou « meilleur » pour les dividendes
 * @param seuilSurBenefice entreprise individuelle à l'IS : les 10 % se
 *        comptent sur le bénéfice net, pas sur un capital (notice Urssaf des
 *        revenus 2026, rubrique ZC). On prend celui de l'exercice lui-même,
 *        c'est-à-dire ce qui est distribué.
 */
export function partage({
    forme,
    enveloppe,
    part,
    capital = 1000,
    activite = "services",
    artisan = false,
    foyer = {},
    imposition = "meilleur",
    seuilSurBenefice = false,
}) {
    const total = positif(enveloppe);
    const p = Math.min(1, Math.max(0, part));
    const r = voletRemuneration({
        forme,
        enveloppeRemuneration: total * p,
        activite,
        artisan,
    });

    const resultat = positif(total - r.cout);
    const is = impotSocietes(resultat);
    const dividendes = positif(resultat - is);

    // EURL : au-delà de 10 % du capital, les dividendes cotisent comme la
    // rémunération. On mesure ce qu'ils ajoutent aux cotisations du gérant.
    let cotisationsDividendes = 0;
    let dividendesCotisants = 0;
    let vieillesseIndependant = forme === "eurl" ? r.detail.vieillesseBase : 0;
    let complementaireIndependant = forme === "eurl" ? r.detail.complementaire : 0;
    if (forme === "eurl" && dividendes > 0) {
        const base = seuilSurBenefice ? dividendes : positif(capital);
        dividendesCotisants = positif(dividendes - SEUIL_DIVIDENDES_EURL * base);
        if (dividendesCotisants > 0) {
            const avec = cotisationsIndependant({
                revenuAvantCotisations: total * p + dividendesCotisants,
                activite,
                artisan,
            });
            cotisationsDividendes = positif(avec.total - r.cotisations);
            // Les dividendes qui cotisent cotisent aussi pour la retraite.
            vieillesseIndependant = avec.vieillesseBase;
            complementaireIndependant = avec.complementaire;
        }
    }
    const prelevementsSociaux = (dividendes - dividendesCotisants) * PFU_SOCIAL;

    const avecPfu = impotAjoute(r.imposable, foyer) + dividendes * PFU_IR;
    const avecBareme = impotAjoute(
        r.imposable + dividendes * (1 - ABATTEMENT_DIVIDENDES_BAREME),
        foyer,
    );
    const mode =
        imposition === "meilleur"
            ? dividendes > 0 && avecBareme < avecPfu
                ? "bareme"
                : "pfu"
            : imposition;
    const impotRevenu = mode === "bareme" ? avecBareme : avecPfu;

    const net =
        r.avantImpot + dividendes - prelevementsSociaux - cotisationsDividendes - impotRevenu;

    return {
        forme,
        part: p,
        enveloppe: total,
        remunerationBrute: r.brut,
        coutRemuneration: r.cout,
        cotisationsRemuneration: r.cotisations,
        remunerationAvantImpot: r.avantImpot,
        resultatSociete: resultat,
        impotSocietes: is,
        dividendes,
        dividendesCotisants,
        cotisationsDividendes,
        prelevementsSociaux,
        impotRevenu,
        imposition: mode,
        trimestres:
            forme === "sasu"
                ? trimestresSalarie(r.brut)
                : trimestresIndependant(vieillesseIndependant),
        // Ce qui part chaque année aux caisses de retraite, base et
        // complémentaire : le montant de la pension en dépend, pas seulement
        // le nombre de trimestres.
        retraite:
            forme === "sasu" ? r.detail.retraite : vieillesseIndependant + complementaireIndependant,
        net,
        // Ce que la collectivité a pris en tout, pour le dire en une phrase.
        prelevements: total - net,
    };
}

/**
 * SASU : le plus petit salaire qui valide les quatre trimestres de l'année, et
 * ce qu'il coûte par rapport au meilleur partage.
 *
 * C'est la question que le tout-dividendes cache. Le lecteur voit qu'il gagne
 * plus sans salaire ; il ne voit pas que la retraite d'une année entière se
 * rachète pour quelques centaines d'euros. L'outil la chiffre au lieu de la
 * sermonner.
 */
export function partageQuatreTrimestres(parametres, meilleur) {
    if (parametres.forme !== "sasu" || !(parametres.enveloppe > 0)) return null;
    const brut = 4 * SALAIRE_UN_TRIMESTRE_2026;
    const cout = cotisationsAssimileSalarie(brut).coutEmployeur;
    if (cout > parametres.enveloppe) return null;
    const point = partage({ ...parametres, part: cout / parametres.enveloppe });
    return {
        point,
        brut: point.remunerationBrute,
        // Ce que les quatre trimestres coûtent, comparé au meilleur partage ;
        // nul quand le meilleur les valide déjà.
        cout: meilleur ? Math.max(0, meilleur.net - point.net) : null,
    };
}

/**
 * Tous les partages, de zéro à cent pour cent de rémunération, et le meilleur.
 *
 * Une exploration par pas plutôt qu'une formule : entre les tranches de
 * l'impôt sur les sociétés, celles du barème, les paliers des cotisations et
 * le seuil des 10 %, la courbe a des cassures, et un optimum local peut en
 * cacher un autre. Cent un points suffisent à ne pas se tromper de région, et
 * un affinage autour du meilleur donne le point exact.
 */
export function meilleurPartage(parametres) {
    const points = [];
    for (let i = 0; i <= 100; i++) {
        points.push(partage({ ...parametres, part: i * PAS_PARTAGE }));
    }
    let meilleur = points.reduce((a, b) => (b.net > a.net ? b : a));

    // Affinage : un dixième de point de part autour du meilleur pas.
    const autour = [];
    for (let d = -10; d <= 10; d++) {
        const part = meilleur.part + d * PAS_PARTAGE / 10;
        if (part >= 0 && part <= 1) autour.push(partage({ ...parametres, part }));
    }
    meilleur = autour.reduce((a, b) => (b.net > a.net ? b : a), meilleur);

    const toutRemuneration = points[points.length - 1];
    const toutDividendes = points[0];
    const gain = meilleur.net - toutRemuneration.net;

    return {
        points,
        meilleur,
        quatreTrimestres:
            trimestresSalarie(meilleur.remunerationBrute) >= 4
                ? null
                : partageQuatreTrimestres(parametres, meilleur),
        toutRemuneration,
        toutDividendes,
        // Ce que le bon partage rapporte par rapport à « tout en salaire »,
        // le réflexe de qui vient de quitter la micro.
        gain,
        partageUtile: gain >= ECART_NEGLIGEABLE,
    };
}

/**
 * EURL ou SASU, à trimestres de retraite égaux.
 *
 * COMPARER LES DEUX « AU MIEUX » SERAIT TRUQUER LA COMPARAISON. Au mieux, la
 * SASU se paie tout en dividendes et ne valide aucun trimestre, quand l'EURL,
 * qui cotise sur tout ce qu'elle verse, en valide quatre. On compare donc
 * l'EURL à son meilleur partage avec la SASU qui valide autant de trimestres
 * qu'elle, au moindre coût ; le tout-dividendes est affiché à part, avec son
 * prix.
 */
export function comparerFormes({ enveloppe, capital = 1000, activite = "services", artisan = false, foyer = {} }) {
    const commun = { enveloppe, activite, artisan, foyer };
    const eurl = meilleurPartage({ ...commun, forme: "eurl", capital });
    const sasu = meilleurPartage({ ...commun, forme: "sasu" });

    const trimestres = eurl.meilleur.trimestres;
    let sasuComparable = sasu.meilleur;
    if (sasu.meilleur.trimestres < trimestres) {
        const brut = trimestres * SALAIRE_UN_TRIMESTRE_2026;
        const cout = cotisationsAssimileSalarie(brut).coutEmployeur;
        sasuComparable =
            cout <= enveloppe
                ? partage({ ...commun, forme: "sasu", part: cout / enveloppe })
                : sasu.toutRemuneration;
    }

    const ecart = sasuComparable.net - eurl.meilleur.net;
    return {
        eurl,
        sasu,
        sasuComparable,
        trimestres,
        // Positif : la SASU laisse plus, à trimestres égaux.
        ecart,
        gagnant: Math.abs(ecart) < ECART_NEGLIGEABLE ? null : ecart > 0 ? "sasu" : "eurl",
        // Ce que la SASU laisserait sans aucun salaire, pour mémoire.
        ecartSansSalaire: sasu.toutDividendes.net - eurl.meilleur.net,
    };
}

/**
 * Le bénéfice à partir duquel la SASU passe devant l'EURL, à trimestres égaux.
 *
 * Balayage de mille en mille, puis la réponse arrondie au millier : une
 * bascule annoncée à l'euro près donnerait une précision que le calcul n'a pas.
 * Nul quand la SASU ne passe jamais devant sur la plage, zéro quand elle y est
 * dès le départ.
 */
export function basculeFormes(parametres, { de = 5000, a = 300000, pas = 1000 } = {}) {
    let precedent = null;
    for (let enveloppe = de; enveloppe <= a; enveloppe += pas) {
        const devant = comparerFormes({ ...parametres, enveloppe }).ecart > 0;
        if (precedent === null && devant) return 0;
        if (precedent === false && devant) return enveloppe;
        precedent = devant;
    }
    return null;
}
